// src/ConsumerApp/Screens/Account/AccountSidebar.js

import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { theme } from '../../../assets/theme/theme';

// Real SVGs from assets/icons
import Package from '../../../assets/icons/Package.svg';
import BuildingOffice from '../../../assets/icons/TransparentBuildingOffice.svg';
import MapPin from '../../../assets/icons/MapPin.svg';
import Percent from '../../../assets/icons/Percent.svg';
import WalletIcon from '../../../assets/icons/Wallet.svg';
import Clipboard from '../../../assets/icons/PClipboardText.svg';
import FileText from '../../../assets/icons/FileText.svg';
import HeartStraight from '../../../assets/icons/bookmark.svg';
import HeadSet from '../../../assets/icons/Headset.svg';
import CaretRight from '../../../assets/icons/CaretRight.svg';
import SignOutIcon from '../../../assets/icons/SignOut.svg';
import UserIcon from '../../../assets/icons/UserCircle.svg';

// Profile API Hook
import { useGetProfileDetails, useCustomerLogout } from '../../api/profile/profile.hooks';

const IMAGE_BASE_URL = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

export default function AccountSidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  // Read stored user session directly from browser storage
  const storedUser = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = storedUser?._id || storedUser?.id;
  const token = storedUser?.token || sessionStorage.getItem('@auth_token') || localStorage.getItem('@auth_token');

  const { data: profileData } = useGetProfileDetails(customerId, token);
  const { mutateAsync: logoutApi } = useCustomerLogout(token);

  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [imageHasError, setImageHasError] = useState(false); // Track avatar image load failure

  const displayName = profileData?.customer_name || profileData?.name || storedUser?.name || storedUser?.customer_name || 'User Name';
  const displayPhone = profileData?.mobile_number || profileData?.phone || storedUser?.phone || storedUser?.mobile_number || 'No phone number';
  const displayTag = profileData?.customer_type || profileData?.buyer_type || storedUser?.customer_type || 'Pro Builder';
  const isVerified = profileData?.customer_status === 3;

  // Extract clean 1 or 2 letter initials (e.g., "Venkata Ramana" -> "VR")
  const initials = useMemo(() => {
    if (!displayName || displayName === 'User Name') return 'U';
    const parts = displayName.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [displayName]);

  const rawImagePath = profileData?.image || profileData?.files?.[0]?.url || profileData?.profile_image;
  const profileImageUri = rawImagePath
    ? rawImagePath.startsWith('http')
      ? rawImagePath
      : `${IMAGE_BASE_URL}${rawImagePath}`
    : null;

  const isProBuilder =
    displayTag === 'Pro Builder' ||
    displayTag === 1 ||
    profileData?.customer_type === 'Pro Builder' ||
    profileData?.customer_type === 1;

  const navMenuItems = useMemo(() => {
    return [
      {
        id: 'orders',
        title: 'Orders',
        subtitle: 'View past orders & tracking',
        icon: Package,
        path: '/consumer/account/orders',
      },
     /*  {
        id: 'delivery-addresses',
        title: 'Delivery Locations',
        subtitle: isProBuilder ? 'Manage multiple sites and addresses' : 'Manage your delivery addresses',
        icon: isProBuilder ? BuildingOffice : MapPin,
        path: '/consumer/account/delivery',
      }, */
      {
        id: 'offers',
        title: 'Offers',
        subtitle: 'View available discounts and coupons',
        icon: Percent,
        path: '/consumer/account/offers',
      },
      {
        id: 'wallet-build-points',
        title: 'Wallet & Brick Points',
        subtitle: 'Check balance, history, and rewards',
        icon: WalletIcon,
        path: '/consumer/account/wallet',
      },
      {
        id: 'rfqs',
        title: 'RFQs',
        subtitle: 'Check your RFQs and best quotes',
        icon: Clipboard,
        path: '/consumer/account/rfqs',
      },
      {
        id: 'invoices-receipts',
        title: 'Invoices & Receipts',
        subtitle: 'View order invoices and receipts',
        icon: FileText,
        path: '/consumer/account/invoices',
      },
      {
        id: 'saved-items',
        title: 'Saved Items',
        subtitle: 'Order from your saved items',
        icon: HeartStraight,
        path: '/consumer/account/saved-items',
        isAccent: true,
      },
      {
        id: 'help-support',
        title: 'Help & Support',
        subtitle: 'Have questions? Talk to us',
        icon: HeadSet,
        path: '/consumer/account/support',
      },
      {
        id: 'general-info',
        title: 'General Info',
        subtitle: 'Brickbucket policies & terms',
        icon: HeadSet,
        path: '/consumer/account/policies',
      },
    ];
  }, [isProBuilder]);

  const handleConfirmSignOut = async () => {
    setIsLoggingOut(true);
    try {
      if (logoutApi) await logoutApi();
    } catch {
      console.warn('Remote session logout skipped.');
    }

    try {
      sessionStorage.clear();
      localStorage.removeItem('userData');
      localStorage.removeItem('@auth_token');
      localStorage.removeItem('@auth_customer');
    } catch (err) {
      console.error('Storage clear error:', err);
    } finally {
      setIsLoggingOut(false);
      setShowSignOutModal(false);
      navigate('/consumer/signin');
    }
  };

  return (
    <aside style={styles.sidebarColumn}>
      {/* ── Profile Header Card ── */}
    <div
  style={styles.profileCard}
  onClick={() => navigate('/consumer/account/profile')}
  role="button"
  tabIndex={0}
>
        <div style={styles.avatarCircle}>
          {profileImageUri && !imageHasError ? (
            <img
              src={profileImageUri}
              alt={displayName}
              style={styles.avatarImg}
              onError={() => setImageHasError(true)}
            />
          ) : (
            <span style={styles.avatarInitial}>{initials}</span>
          )}
        </div>

        <div style={styles.profileDetails}>
          <h3 style={styles.userName}>{displayName}</h3>
          <div style={styles.metaRow}>
            <span style={styles.phoneText}>{displayPhone}</span>
            <span style={styles.bulletDot}>•</span>
            <span style={{ ...styles.verifiedText, color: isVerified ? '#16A34A' : '#707B8C' }}>
              {isVerified ? 'Verified' : 'Not verified'}
            </span>
          </div>
          <div style={styles.roleTag}>
            <span style={styles.roleTagText}>{displayTag}</span>
          </div>
        </div>

        <img src={CaretRight} alt="›" style={styles.headerChevron} />
      </div>

      {/* ── Menu Items ── */}
      <nav style={styles.menuStack}>
        {navMenuItems.map((item) => {
          const isActive =
            location.pathname === item.path ||
            location.pathname.startsWith(`${item.path}/`);

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => navigate(item.path)}
              style={{
                ...styles.menuCardBtn,
                borderColor: isActive ? theme.colors.brand : theme.colors.grey005,
                backgroundColor: isActive ? '#FFF7F7' : theme.colors.pureWhite,
              }}
            >
              <div style={styles.menuLeftCluster}>
                <div
                  style={{
                    ...styles.menuIconContainer,
                    backgroundColor: isActive ? '#FDEEEA' : '#F8FAFC',
                  }}
                >
                  <img
                    src={item.icon}
                    alt={item.title}
                    style={{
                      ...styles.menuIconImg,
                      filter: item.isAccent
                        ? 'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)'
                        : isActive
                        ? 'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)'
                        : 'none',
                    }}
                  />
                </div>
                <div style={styles.menuTextCluster}>
                  <span
                    style={{
                      ...styles.menuTitle,
                      color: isActive ? theme.colors.brand : theme.colors.charcoal,
                    }}
                  >
                    {item.title}
                  </span>
                  <span style={styles.menuSubtitle}>{item.subtitle}</span>
                </div>
              </div>
              <img
                src={CaretRight}
                alt="›"
                style={{
                  ...styles.navChevron,
                  opacity: isActive ? 0.9 : 0.35,
                }}
              />
            </button>
          );
        })}

        {/* ── Sign Out Button ── */}
        <button
          type="button"
          onClick={() => setShowSignOutModal(true)}
          style={styles.signOutBtn}
        >
          <div style={styles.menuLeftCluster}>
            <div style={styles.signOutIconWrap}>
              <img
                src={SignOutIcon || UserIcon}
                alt="Sign Out"
                style={{
                  width: 18,
                  height: 18,
                  filter: 'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)',
                }}
              />
            </div>
            <span style={styles.signOutText}>Sign Out</span>
          </div>
        </button>
      </nav>

      {/* ── Sign Out Modal ── */}
      {showSignOutModal && (
        <div style={styles.modalOverlay} onClick={() => !isLoggingOut && setShowSignOutModal(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h4 style={styles.modalTitle}>Sign Out</h4>
            <p style={styles.modalSub}>Are you sure you want to sign out of your account?</p>

            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.cancelBtn}
                disabled={isLoggingOut}
                onClick={() => setShowSignOutModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={styles.confirmBtn}
                disabled={isLoggingOut}
                onClick={handleConfirmSignOut}
              >
                {isLoggingOut ? 'Signing out...' : 'Sign Out'}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}

const styles = {
  sidebarColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[150],
    width: '100%',
    maxWidth: 320,
  },
  profileCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 16px',
    backgroundColor: theme.colors.pureWhite,
    borderRadius: theme.radius[16],
    border: `1px solid ${theme.colors.grey005}`,
    cursor: 'pointer',
    userSelect: 'none',
    boxSizing: 'border-box',
    ...theme.shadows.fieldShadow,
  },
  avatarCircle: {
    width: 50,
    height: 50,
    borderRadius: '50%',
    backgroundColor: '#FDEEEA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarInitial: {
    fontFamily: theme.typography.headingLG.fontFamily,
    fontSize: 16,
    color: theme.colors.primaryRed,
    fontWeight: '700',
    letterSpacing: '0.5px',
    lineHeight: 1,
  },
  profileDetails: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  userName: {
    ...theme.typography.headingMD,
    color: theme.colors.charcoal,
    fontWeight: '700',
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: 14.5,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 2,
  },
  phoneText: {
    ...theme.typography.bodySM,
    color: theme.colors.concrete,
    fontSize: 11.5,
  },
  bulletDot: {
    fontSize: 9,
    color: theme.colors.concrete,
  },
  verifiedText: {
    ...theme.typography.bodySM,
    fontSize: 11.5,
    fontWeight: '600',
  },
  roleTag: {
    alignSelf: 'flex-start',
    backgroundColor: '#FDEEEA',
    borderRadius: theme.radius[8],
    padding: '2px 8px',
    marginTop: 5,
  },
  roleTagText: {
    ...theme.typography.bodySM,
    color: theme.colors.primaryRed,
    fontWeight: '600',
    fontSize: 10.5,
  },
  headerChevron: {
    width: 14,
    height: 14,
    flexShrink: 0,
    opacity: 0.35,
  },
  menuStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  menuCardBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    borderRadius: theme.radius[12],
    border: `1px solid ${theme.colors.grey005}`,
    backgroundColor: theme.colors.pureWhite,
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    boxSizing: 'border-box',
    outline: 'none',
    ...theme.shadows.fieldShadow,
  },
  menuLeftCluster: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  menuIconContainer: {
    width: 36,
    height: 36,
    borderRadius: theme.radius[8],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  menuIconImg: {
    width: 18,
    height: 18,
    objectFit: 'contain',
    display: 'block',
  },
  menuTextCluster: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  menuTitle: {
    ...theme.typography.bodyMD,
    fontWeight: '600',
    fontSize: 13.5,
  },
  menuSubtitle: {
    ...theme.typography.bodySM,
    color: theme.colors.concrete,
    fontSize: 11,
    marginTop: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  navChevron: {
    width: 13,
    height: 13,
    flexShrink: 0,
  },
  signOutBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '12px 14px',
    borderRadius: theme.radius[12],
    border: '1px solid #FEE2E2',
    backgroundColor: '#FFFDFD',
    cursor: 'pointer',
    marginTop: 6,
    outline: 'none',
    transition: 'background-color 0.15s ease',
  },
  signOutIconWrap: {
    width: 36,
    height: 36,
    borderRadius: theme.radius[8],
    backgroundColor: '#FEF2F2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  signOutText: {
    fontSize: 13.5,
    fontWeight: '700',
    color: theme.colors.primaryRed || '#C7061F',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(2px)',
    padding: 16,
  },
  modalCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: '20px 24px',
    width: '100%',
    maxWidth: 340,
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  modalTitle: {
    margin: '0 0 6px 0',
    fontSize: 16,
    fontWeight: '700',
    color: theme.colors.charcoal,
  },
  modalSub: {
    margin: '0 0 20px 0',
    fontSize: 12.5,
    color: theme.colors.concrete,
    lineHeight: '17px',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
  },
  cancelBtn: {
    flex: 1,
    padding: '9px 14px',
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    backgroundColor: '#FFFFFF',
    color: theme.colors.charcoal,
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer',
  },
  confirmBtn: {
    flex: 1,
    padding: '9px 14px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: theme.colors.primaryRed || '#C7061F',
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700',
    cursor: 'pointer',
  },
};